import axios from "axios";

import { apiConfig } from "../config/authConfig";

import { CredentialManager } from "../classes/credentialManager";
import { IManifest, IManifestUpdateRequest as IManifestSaveRequest } from "../types/manifest";

export namespace ManifestService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Manifest.
	 * @param data Manifest response data from an API.
	 **********/
	export function mapManifest(data: any) {
		let manifest: IManifest = {
			id: data.id,
			isHazardous: data.isHazardous,
			number: data.number,
			manifestVendorId: data.manifestVendorId,
			secondaryTransporterId: data.secondaryTransporterId,
			customerId: data.customerId,
			remoteShipmentId: data.remoteShipmentId,
			shipmentId: data.shipmentId,
			createdOn: data.createdOn ? new Date(data.createdOn) : undefined,
			createdBy: data.createdBy
		}

		return manifest;
	}

	/**********
	 * Convert an array of api response objects to an array of Manifests.
	 * @param data Manifest data from an API response.
	 **********/
	export function mapManifests(data: any) {
		let manifests: IManifest[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				manifests.push(mapManifest(item));
			})
		}

		return manifests;
	}

	/**********
	 * Gets all manifests by load id.
	 * @param loadId
	 **********/
	export async function getManifestsByLoad(loadId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/manifest/load/" + loadId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all manifests by shipment id.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getManifestsByShipment(shipmentId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/manifest/shipment/" + shipmentId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a manifest.
	 * @param manifestId The id of the manifest.
	 **********/
	export async function getManifest(manifestId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/manifest/" + manifestId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a manifest.
	 * @param request The manifest to add.
	 **********/
	export async function addManifest(request: IManifestSaveRequest) {
		const apiResponse = axios.put(
			apiConfig.default + "/manifest",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a manifest.
	 * @param request The manifest to update.
	 **********/
	export async function updateManifest(request: IManifestSaveRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/manifest",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Delete a manifest and unassign all of its materials.
	 * @param manifestId The id of the manifest to delete.
	 **********/
	export async function deleteManifest(manifestId: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/manifest/" + manifestId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}